import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl, categories } from '@/mocks/categories';
import { useState, useRef, useEffect } from 'react';

export default function Navbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setMobileOpen(false);
  };

  const closeMobile = () => setMobileOpen(false);

  const guideLinks = [
    { to: '/oopbuy-spreadsheet-guide', label: 'Anfänger Guide' },
    { to: '/versand-guide', label: t('nav.versand') },
    { to: '/qc-guide', label: t('nav.qc') },
    { to: '/oopbuy-vs-pandabuy', label: t('nav.compare') },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/70 transition-all duration-300">
      <div ref={menuRef} className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" onClick={closeMobile} className="flex items-center gap-2 group flex-shrink-0">
            <span className="text-lg md:text-xl font-heading font-semibold text-foreground-900 whitespace-nowrap">
              OOPBuy <span className="text-accent-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              {t('nav.home')}
            </Link>

            {/* Einkaufsführer Dropdown */}
            <div className="relative group">
              <button className="text-sm font-medium text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer flex items-center gap-1">
                <i className="ri-book-open-line mr-1.5"></i>
                {t('nav.einkaufsfuehrer')}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {guideLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors first:rounded-t-xl last:rounded-b-xl whitespace-nowrap cursor-pointer"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Kategorien Dropdown */}
            <div className="relative group">
              <button className="text-sm font-medium text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer flex items-center gap-1">
                <i className="ri-grid-line mr-1.5"></i>
                {t('nav.categories')}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 w-52 bg-background-50 rounded-xl border border-background-200/70 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.nameKey}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center justify-between whitespace-nowrap cursor-pointer"
                  >
                    <span>{t(cat.nameKey)}</span>
                    <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                  </button>
                ))}
              </div>
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-external-link-line mr-1.5"></i>
              Spreadsheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer ml-2"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile: Buy Now + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setMobileOpen(prev => !prev)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileOpen && (
          <div id="mobile-menu-panel" className="md:hidden border-t border-background-200/70 bg-background-50">
            <div className="px-4 py-3 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
              {/* Home */}
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                {t('nav.home')}
              </Link>

              {/* Einkaufsführer Accordion */}
              <div>
                <button
                  onClick={() => setGuidesOpen(!guidesOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-book-open-line"></i>
                    {t('nav.einkaufsfuehrer')}
                  </span>
                  <i className={`${guidesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                </button>
                {guidesOpen && (
                  <div className="ml-4 pl-4 border-l-2 border-background-200/70 mt-1 flex flex-col gap-0.5">
                    {guideLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={closeMobile}
                        className="px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line mr-1 text-foreground-400"></i>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Kategorien Accordion */}
              <div>
                <button
                  onClick={() => setCatsOpen(!catsOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-grid-line"></i>
                    {t('nav.categories')}
                  </span>
                  <i className={`${catsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                </button>
                {catsOpen && (
                  <div className="ml-4 pl-4 border-l-2 border-background-200/70 mt-1 flex flex-col gap-0.5">
                    {categories.map((cat) => (
                      <button
                        key={cat.nameKey}
                        onClick={() => handleCategoryClick(cat.url)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-accent-500 transition-colors flex items-center justify-between cursor-pointer"
                      >
                        <span>{t(cat.nameKey)}</span>
                        <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Spreadsheet Link */}
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                Spreadsheet
              </a>

              {/* Divider */}
              <div className="border-t border-background-200/70 my-1"></div>

              {/* OOPBuy Spreadsheet Button */}
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-rocket-line"></i>
                OOPBuy Spreadsheet
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}